import React, { useEffect, useState, createContext, useContext, memo, Component } from 'react';
// Types for our ritual system
export interface Ritual {
  id: string;
  title: string;
  description: string;
  type: 'daily' | 'weekly' | 'fun' | 'monetization' | 'premium';
  progress: number;
  target: number;
  completed: boolean;
  reward: {
    type: 'tokens' | 'gems' | 'xp' | 'lootbox';
    amount: number;
  };
  icon: string; // Lucide icon name
  category: 'chat' | 'feed' | 'marketplace' | 'games' | 'profile' | 'general' | 'playroom' | 'media' | 'dickpic' | 'unleash';
  isNew?: boolean;
  isHot?: boolean;
  isPremium?: boolean;
  requiredForLootbox?: boolean;
  premiumOnly?: boolean;
  // Rotation metadata
  lastShown?: number; // timestamp when this ritual was last shown
  tags?: string[]; // for categorization (e.g., 'light', 'entertainment', 'content-generation')
  rotationGroup?: 'daily-core' | 'fun' | 'weekly' | 'premium'; // to track which pool this ritual belongs to
}
export type LootboxReward = {
  id: string;
  title: string;
  description: string;
  type: 'tokens' | 'gems' | 'vip' | 'skin' | 'chatTime' | 'picture' | 'video';
  amount?: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  imageUrl?: string;
};
interface RitualContextType {
  dailyRituals: Ritual[];
  weeklyRituals: Ritual[];
  funRituals: Ritual[];
  premiumRituals: Ritual[];
  streakCount: number;
  weeklyStreakCount: number;
  hasUnclaimedRewards: boolean;
  pendingLootbox: boolean;
  showRitualTracker: boolean;
  toggleRitualTracker: () => void;
  updateRitualProgress: (ritualId: string, progress: number) => void;
  completeRitual: (ritualId: string) => void;
  claimDailyRewards: () => void;
  claimWeeklyLootbox: () => Promise<LootboxReward>;
  skipRitual: (ritualId: string) => void;
  todayCompletedCount: number;
  totalDailyRituals: number;
  weeklyCompletedCount: number;
  totalWeeklyRituals: number;
  areAllRequiredRitualsCompleted: () => boolean;
  rotateRituals: () => void; // New function to manually trigger rotation
  lastDailyRotation: number; // Timestamp of last daily rotation
  lastWeeklyRotation: number; // Timestamp of last weekly rotation
  allRitualProgress: Record<string, {
    progress: number;
    completed: boolean;
  }>; // Track progress for all rituals
}
const RitualContext = createContext<RitualContextType | undefined>(undefined);
// Create the full pools of rituals
// Daily Core Rituals Pool (100)
const dailyCoreRitualsPool: Ritual[] = [{
  id: 'daily-core-1',
  title: 'Login once today',
  description: 'Log in to the app once today',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'LogIn',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-2',
  title: 'Login twice today',
  description: 'Log in to the app twice today',
  type: 'daily',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'LogIn',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-3',
  title: 'Login three times today',
  description: 'Log in to the app three times today',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'LogIn',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-4',
  title: 'Update your profile picture',
  description: 'Change or update your profile picture',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'UserCircle',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-5',
  title: 'Update your bio',
  description: 'Update your profile bio information',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'FileText',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-6',
  title: 'Adjust privacy settings',
  description: 'Review and adjust your privacy settings',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Shield',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-7',
  title: 'Complete quick questionnaire',
  description: 'Answer a few quick questions about your preferences',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'ClipboardCheck',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-8',
  title: 'Complete full questionnaire',
  description: 'Complete a comprehensive questionnaire about your preferences',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 50
  },
  icon: 'ClipboardList',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-9',
  title: 'Verify your age',
  description: 'Complete age verification process',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'BadgeCheck',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-10',
  title: 'Enable notifications',
  description: 'Enable push notifications to stay updated',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Bell',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-11',
  title: 'Favorite one Angel',
  description: 'Add one Angel to your favorites',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Heart',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-12',
  title: 'Favorite three Angels',
  description: 'Add three Angels to your favorites',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Heart',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-13',
  title: 'Bookmark one post',
  description: 'Save one post to your bookmarks',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'Bookmark',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-14',
  title: 'Bookmark five posts',
  description: 'Save five posts to your bookmarks',
  type: 'daily',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'Bookmark',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-15',
  title: 'Send your first chat message',
  description: 'Send a message in a private chat',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'MessageSquare',
  category: 'chat',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-16',
  title: 'Send ten chat messages',
  description: 'Send ten messages in private chats',
  type: 'daily',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'MessageSquare',
  category: 'chat',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-17',
  title: 'Open the Playroom once',
  description: 'Join the Playroom group chat once',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Users',
  category: 'playroom',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-18',
  title: 'Join three Playroom sessions',
  description: 'Join three different Playroom group chats',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 40
  },
  icon: 'Users',
  category: 'playroom',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-19',
  title: 'Invite a friend to Playroom',
  description: 'Invite a friend to join a Playroom session',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'UserPlus',
  category: 'playroom',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-20',
  title: 'Cast one vote in Hot or Not',
  description: 'Vote on one profile in Hot or Not',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'ThumbsUp',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-21',
  title: 'Cast five votes in Hot or Not',
  description: 'Vote on five profiles in Hot or Not',
  type: 'daily',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'ThumbsUp',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-22',
  title: 'Like one post in the feed',
  description: 'Like one post in your social feed',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 5
  },
  icon: 'Heart',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-23',
  title: 'Like ten posts in the feed',
  description: 'Like ten posts in your social feed',
  type: 'daily',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Heart',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-24',
  title: 'Comment on one post',
  description: 'Leave a comment on a post in your feed',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'MessageCircle',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-25',
  title: 'Comment on three posts',
  description: 'Leave comments on three posts in your feed',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'MessageCircle',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-26',
  title: 'Share one post',
  description: 'Share a post with your friends',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Share2',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-27',
  title: "View Angel Harper's feed",
  description: "Check out Harper's profile and content",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'User',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-28',
  title: "View Angel Zoey's feed",
  description: "Check out Zoey's profile and content",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'User',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-29',
  title: "View Angel Sophia's feed",
  description: "Check out Sophia's profile and content",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'User',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-30',
  title: "View Angel Lana's feed",
  description: "Check out Lana's profile and content",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'User',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-31',
  title: 'Purchase 100 tokens',
  description: 'Buy 100 tokens from the token store',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'CreditCard',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-32',
  title: 'Purchase 500 tokens',
  description: 'Buy 500 tokens from the token store',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 50
  },
  icon: 'CreditCard',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-33',
  title: 'Spend 100 tokens',
  description: 'Use 100 tokens on content or features',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 10
  },
  icon: 'Coins',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-34',
  title: 'Spend 500 tokens',
  description: 'Use 500 tokens on content or features',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 25
  },
  icon: 'Coins',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-35',
  title: 'Spend 1000 tokens',
  description: 'Use 1000 tokens on content or features',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 50
  },
  icon: 'Coins',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-36',
  title: 'Unlock one premium content',
  description: 'Unlock a premium content item',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Unlock',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-37',
  title: 'Unlock three premium content',
  description: 'Unlock three premium content items',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 40
  },
  icon: 'Unlock',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-38',
  title: 'Watch a Welcome Video',
  description: "Watch the app's welcome video",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'PlayCircle',
  category: 'media',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-39',
  title: 'Open Reward Hub',
  description: 'Visit the Reward Hub to see your rewards',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'Gift',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-40',
  title: 'Claim one lootbox',
  description: 'Claim a lootbox reward',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Package',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-41',
  title: 'Open three lootboxes',
  description: 'Open three lootbox rewards',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Package',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-42',
  title: 'Spin the Wheel once',
  description: 'Try your luck on the prize wheel',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'RefreshCw',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-43',
  title: 'Spin the Wheel three times',
  description: 'Spin the prize wheel three times',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'RefreshCw',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-44',
  title: 'Rate one photo',
  description: 'Rate a photo in the app',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'Star',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-45',
  title: 'Rate ten photos',
  description: 'Rate ten photos in the app',
  type: 'daily',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'Star',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-46',
  title: 'Buy one Angel skin',
  description: 'Purchase a skin for your favorite Angel',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Shirt',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-47',
  title: 'Equip one Angel skin',
  description: 'Apply a skin to your favorite Angel',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Shirt',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-48',
  title: 'Unleash a personal fantasy',
  description: 'Create a personal fantasy scenario',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Sparkles',
  category: 'unleash',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-49',
  title: 'Unleash three personal fantasies',
  description: 'Create three personal fantasy scenarios',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 50
  },
  icon: 'Sparkles',
  category: 'unleash',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-50',
  title: 'Unleash a personal Angel',
  description: 'Create your own personalized Angel',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 40
  },
  icon: 'Sparkles',
  category: 'unleash',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-51',
  title: 'Send one Dickpic',
  description: 'Send a dickpic for rating',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Camera',
  category: 'dickpic',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-52',
  title: 'Send three Dickpics',
  description: 'Send three dickpics for rating',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Camera',
  category: 'dickpic',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-53',
  title: 'Use Friends Opinion once',
  description: "Get friends' opinions on your dickpic",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Users',
  category: 'dickpic',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-54',
  title: 'Use Friends Opinion three times',
  description: "Get friends' opinions on your dickpic three times",
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 40
  },
  icon: 'Users',
  category: 'dickpic',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-55',
  title: 'Join one Fan Club',
  description: 'Join a Fan Club of your favorite Angel',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Crown',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-56',
  title: 'Join three Fan Clubs',
  description: 'Join three Fan Clubs of your favorite Angels',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 40
  },
  icon: 'Crown',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-57',
  title: 'Participate in one poll',
  description: 'Vote in a community poll',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'BarChart2',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-58',
  title: 'Participate in three polls',
  description: 'Vote in three community polls',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'BarChart2',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-59',
  title: 'Create one poll',
  description: 'Create a poll for the community',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'BarChart',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-60',
  title: 'Generate one Angel image',
  description: 'Generate a custom Angel image',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'Image',
  category: 'media',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-61',
  title: 'Generate three Angel images',
  description: 'Generate three custom Angel images',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 50
  },
  icon: 'Image',
  category: 'media',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-62',
  title: 'Generate one Angel video',
  description: 'Generate a custom Angel video',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 35
  },
  icon: 'Video',
  category: 'media',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-63',
  title: 'Generate three Angel videos',
  description: 'Generate three custom Angel videos',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Video',
  category: 'media',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-64',
  title: 'Enter VR/AR mode once',
  description: 'Experience the app in VR/AR mode',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'Glasses',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-65',
  title: 'Enter VR/AR mode three times',
  description: 'Experience the app in VR/AR mode three times',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 50
  },
  icon: 'Glasses',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-66',
  title: 'Interact with Interactive Toy',
  description: 'Connect and use an interactive toy',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Bluetooth',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-67',
  title: 'Send one gift in chat',
  description: 'Send a gift to an Angel in chat',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Gift',
  category: 'chat',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-68',
  title: 'Send three gifts in chat',
  description: 'Send three gifts to Angels in chat',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Gift',
  category: 'chat',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-69',
  title: 'Reach relationship level 1',
  description: 'Reach level 1 relationship with any Angel',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Heart',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-70',
  title: 'Reach relationship level 3',
  description: 'Reach level 3 relationship with any Angel',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 40
  },
  icon: 'Heart',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-71',
  title: 'Reach relationship level 5',
  description: 'Reach level 5 relationship with any Angel',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 60
  },
  icon: 'Heart',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-72',
  title: 'Claim streak bonus',
  description: 'Claim your daily login streak bonus',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'Zap',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-73',
  title: 'Check subscription options',
  description: 'View the available subscription plans',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'CreditCard',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-74',
  title: 'Upgrade to Premium',
  description: 'Subscribe to the Premium membership',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 50
  },
  icon: 'Award',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-75',
  title: 'Upgrade to VIP',
  description: 'Subscribe to the VIP membership',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 100
  },
  icon: 'Crown',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-76',
  title: 'Engage in AR Playroom',
  description: 'Join an augmented reality Playroom session',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 35
  },
  icon: 'Glasses',
  category: 'playroom',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-77',
  title: 'Unlock special fan perk',
  description: "Unlock a special perk in an Angel's Fan Club",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Key',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-78',
  title: 'Share feedback',
  description: 'Share your feedback about the app',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'MessageSquare',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-79',
  title: 'Report a bug',
  description: 'Report a bug or issue in the app',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'AlertTriangle',
  category: 'profile',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-80',
  title: 'Explore Creator Marketplace',
  description: 'Browse the Creator Marketplace',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'ShoppingBag',
  category: 'marketplace',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-81',
  title: 'Support a creator',
  description: 'Purchase content from a creator',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'DollarSign',
  category: 'marketplace',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-82',
  title: 'Post content to feed',
  description: 'Post content to your social feed',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Send',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-83',
  title: 'Post three content pieces',
  description: 'Post three content pieces to your social feed',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 40
  },
  icon: 'Send',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-84',
  title: 'Explore Creator Studio',
  description: 'Visit the Creator Studio',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Compass',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-85',
  title: 'Create mini content',
  description: 'Create a piece of mini content in Creator Studio',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'Edit',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-86',
  title: 'Play one mini-game',
  description: 'Play a mini-game in the app',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Gamepad',
  category: 'games',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-87',
  title: 'Play three mini-games',
  description: 'Play three mini-games in the app',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Gamepad',
  category: 'games',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-88',
  title: 'Win a mini-game',
  description: 'Win a mini-game in the app',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'Award',
  category: 'games',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-89',
  title: 'Watch one yoga post',
  description: 'Watch a yoga video post',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Video',
  category: 'media',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-90',
  title: 'Watch three yoga posts',
  description: 'Watch three yoga video posts',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Video',
  category: 'media',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-91',
  title: 'Follow Angel Mia Khalifa',
  description: "Follow Mia Khalifa's profile",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'UserPlus',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-92',
  title: 'Follow Angel Kim Holland',
  description: "Follow Kim Holland's profile",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'UserPlus',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-93',
  title: 'Follow Angel Bonny Blue',
  description: "Follow Bonny Blue's profile",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'UserPlus',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-94',
  title: 'Follow Angel Bella Blue',
  description: "Follow Bella Blue's profile",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'UserPlus',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-95',
  title: 'Like one Angel Story',
  description: 'Like a story posted by an Angel',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'Heart',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-96',
  title: 'Like three Angel Stories',
  description: 'Like three stories posted by Angels',
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Heart',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-97',
  title: 'Comment on one Angel Story',
  description: "Leave a comment on an Angel's story",
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'MessageCircle',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-98',
  title: 'Comment on three Angel Stories',
  description: "Leave comments on three Angels' stories",
  type: 'daily',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'MessageCircle',
  category: 'feed',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-99',
  title: 'Complete daily streak',
  description: 'Complete all your daily rituals',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 25
  },
  icon: 'Award',
  category: 'general',
  rotationGroup: 'daily-core'
}, {
  id: 'daily-core-100',
  title: 'Finish all 15 daily rituals once',
  description: 'Complete all 15 daily rituals in a single day',
  type: 'daily',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 200
  },
  icon: 'CheckCircle',
  category: 'general',
  rotationGroup: 'daily-core'
}];
// Fun Rituals Pool (100)
const funRitualsPool: Ritual[] = [{
  id: 'fun-1',
  title: "Vote in Angel's outfit poll",
  description: 'Vote on what outfit your Angel should wear next',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Shirt',
  category: 'feed',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-2',
  title: 'Answer "Would you rather…" question',
  description: 'Answer a fun "Would you rather" question from an Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'HelpCircle',
  category: 'feed',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-3',
  title: 'Play "Guess the Angel" trivia',
  description: 'Play a round of "Guess the Angel" trivia',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Brain',
  category: 'games',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-4',
  title: 'Complete 1 meme challenge',
  description: 'Create or share a meme based on the daily challenge',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Laugh',
  category: 'feed',
  tags: ['light', 'entertainment', 'content-generation'],
  rotationGroup: 'fun'
}, {
  id: 'fun-5',
  title: 'Create 1 fantasy scenario',
  description: 'Create a custom fantasy scenario with an Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Sparkles',
  category: 'media',
  tags: ['content-generation'],
  isPremium: true,
  premiumOnly: true,
  rotationGroup: 'fun'
}, {
  id: 'fun-6',
  title: 'Create 3 fantasy scenarios',
  description: 'Create 3 custom fantasy scenarios with Angels',
  type: 'fun',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Sparkles',
  category: 'media',
  tags: ['content-generation'],
  isPremium: true,
  premiumOnly: true,
  rotationGroup: 'fun'
}, {
  id: 'fun-7',
  title: 'Send a sticker pack',
  description: 'Send a sticker pack to any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'Sticker',
  category: 'chat',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-8',
  title: 'React to 5 random posts',
  description: "React to 5 posts from Angels you don't follow",
  type: 'fun',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Shuffle',
  category: 'feed',
  tags: ['light'],
  rotationGroup: 'fun'
}, {
  id: 'fun-9',
  title: 'Spin a virtual wheel',
  description: 'Spin the virtual wheel for a random reward',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'RotateCw',
  category: 'games',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-10',
  title: 'Send 1 playful dare to an Angel',
  description: 'Send a playful dare to any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Zap',
  category: 'chat',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-11',
  title: 'Accept 1 playful dare',
  description: 'Accept and complete a playful dare from an Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'CheckCircle2',
  category: 'chat',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-12',
  title: 'Watch a blooper video',
  description: 'Watch a blooper video from any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'Video',
  category: 'feed',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-13',
  title: 'Watch a behind-the-scenes video',
  description: 'Watch a behind-the-scenes video from any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Video',
  category: 'feed',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-14',
  title: 'Play 1 mini-game with Angel',
  description: 'Play a mini-game with any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 20
  },
  icon: 'Gamepad2',
  category: 'games',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-15',
  title: 'Join 1 group fun poll',
  description: 'Participate in a group poll with multiple Angels',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'BarChart2',
  category: 'feed',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-16',
  title: 'Unlock 1 fun parody scenario',
  description: 'Unlock a fun parody scenario with any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'Theater',
  category: 'marketplace',
  tags: ['content-generation'],
  isPremium: true,
  premiumOnly: true,
  rotationGroup: 'fun'
}, {
  id: 'fun-17',
  title: 'Generate 1 comic-style image',
  description: 'Generate a comic-style image with any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 30
  },
  icon: 'Palette',
  category: 'media',
  tags: ['content-generation'],
  isPremium: true,
  premiumOnly: true,
  rotationGroup: 'fun'
}, {
  id: 'fun-18',
  title: 'Generate 1 GIF',
  description: 'Generate a custom GIF with any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 25
  },
  icon: 'Film',
  category: 'media',
  tags: ['content-generation'],
  isPremium: true,
  premiumOnly: true,
  rotationGroup: 'fun'
}, {
  id: 'fun-19',
  title: "Vote in Angel's music poll",
  description: 'Vote on what music your Angel should listen to',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'Music',
  category: 'feed',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-20',
  title: "Vote in Angel's hobby poll",
  description: 'Vote on what hobby your Angel should try next',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'Palette',
  category: 'feed',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
},
// Adding more rituals to simulate the full 100 pool
// In a real implementation, we would add all 100 rituals
{
  id: 'fun-21',
  title: 'Post 1 playful memory',
  description: 'Share a playful memory with an Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Clock',
  category: 'feed',
  tags: ['content-generation'],
  rotationGroup: 'fun'
}, {
  id: 'fun-22',
  title: 'Share 1 playlist',
  description: 'Share a playlist with any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'ListMusic',
  category: 'feed',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-23',
  title: 'Watch a fantasy teaser video',
  description: 'Watch a teaser video for an upcoming fantasy scenario',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'Film',
  category: 'feed',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-24',
  title: 'Send "spin the bottle" emoji',
  description: 'Send a "spin the bottle" emoji to any Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 10
  },
  icon: 'RotateCw',
  category: 'chat',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}, {
  id: 'fun-25',
  title: 'Participate in "Angel quiz"',
  description: 'Take a quiz about your favorite Angel',
  type: 'fun',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 15
  },
  icon: 'HelpCircle',
  category: 'games',
  tags: ['light', 'entertainment'],
  rotationGroup: 'fun'
}
// Additional fun rituals to fill out the pool
];
// Weekly Rituals Pool (100)
const weeklyRitualsPool: Ritual[] = [{
  id: 'weekly-1',
  title: 'Login 7 times in a week',
  description: 'Log in to the app 7 times this week',
  type: 'weekly',
  progress: 0,
  target: 7,
  completed: false,
  reward: {
    type: 'lootbox',
    amount: 1
  },
  icon: 'LogIn',
  category: 'profile',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-2',
  title: 'Send 100 messages in a week',
  description: 'Send a total of 100 messages this week',
  type: 'weekly',
  progress: 0,
  target: 100,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 100
  },
  icon: 'MessageSquare',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-21',
  title: 'Login 7 days in a row',
  description: 'Log in to the app 7 days in a row this week',
  type: 'weekly',
  progress: 0,
  target: 7,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 100
  },
  icon: 'LogIn',
  category: 'profile',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-22',
  title: 'Spin the Enhanced Wheel',
  description: 'Spin the Enhanced Spin Wheel 20 times this week',
  type: 'weekly',
  progress: 0,
  target: 20,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'RotateCw',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-23',
  title: 'Join Playroom sessions',
  description: 'Join 5 Playroom sessions this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'gems',
    amount: 50
  },
  icon: 'Users',
  category: 'playroom',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-24',
  title: 'Send gifts to Angels',
  description: 'Send 10 gifts to Angels this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Gift',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-25',
  title: 'Unlock Angel skins',
  description: 'Unlock 3 Angel skins this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'gems',
    amount: 75
  },
  icon: 'Shirt',
  category: 'marketplace',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-26',
  title: 'Vote in HotOrNot polls',
  description: 'Vote in 20 HotOrNot polls this week',
  type: 'weekly',
  progress: 0,
  target: 20,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 60
  },
  icon: 'ThumbsUp',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-27',
  title: 'Create custom polls',
  description: 'Create 3 custom polls in PollGenerator this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'BarChart2',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-28',
  title: 'Play Angel Match game',
  description: 'Play Angel Match game 10 times this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Gamepad',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-29',
  title: 'Bookmark Angel posts',
  description: 'Bookmark 10 Angel posts this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 50
  },
  icon: 'Bookmark',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-30',
  title: 'React with sparkles',
  description: 'React with sparkles on 25 posts this week',
  type: 'weekly',
  progress: 0,
  target: 25,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 65
  },
  icon: 'Sparkles',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-31',
  title: 'Post updates in feed',
  description: 'Post 5 updates in the feed this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Send',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-32',
  title: 'Rate selfies in HotOrNot',
  description: 'Rate 25 selfies in HotOrNot this week',
  type: 'weekly',
  progress: 0,
  target: 25,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Star',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-33',
  title: 'Open Daily Lootboxes',
  description: 'Open 7 Daily Lootboxes this week',
  type: 'weekly',
  progress: 0,
  target: 7,
  completed: false,
  reward: {
    type: 'gems',
    amount: 50
  },
  icon: 'Package',
  category: 'general',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-34',
  title: 'Dress up Angels',
  description: 'Dress up 3 Angels this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Shirt',
  category: 'marketplace',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-35',
  title: 'Send stickers in chat',
  description: 'Send 10 stickers in chat this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 60
  },
  icon: 'Sticker',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-36',
  title: 'Join Multi-Angel Playroom',
  description: 'Join 3 Multi-Angel Playroom sessions this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'gems',
    amount: 75
  },
  icon: 'Users',
  category: 'playroom',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-37',
  title: 'Complete Questionnaires',
  description: 'Complete 2 Questionnaires this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 100
  },
  icon: 'ClipboardList',
  category: 'profile',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-38',
  title: 'Send voice notes',
  description: 'Send 5 voice notes to Angels this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Mic',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-39',
  title: 'Use AR filters',
  description: 'Use 5 AR filters with Angels this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'gems',
    amount: 60
  },
  icon: 'Camera',
  category: 'vr_ar',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-40',
  title: 'Try VR environments',
  description: 'Try 3 VR environments this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'gems',
    amount: 80
  },
  icon: 'Glasses',
  category: 'vr_ar',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-41',
  title: 'Equip new Angel skins',
  description: 'Equip 5 new Angel skins this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 90
  },
  icon: 'Shirt',
  category: 'marketplace',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-42',
  title: 'Send roses',
  description: 'Send 7 roses this week',
  type: 'weekly',
  progress: 0,
  target: 7,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Heart',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-43',
  title: 'Unlock fantasy scenarios',
  description: 'Unlock 2 fantasy scenarios this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 70
  },
  icon: 'Sparkles',
  category: 'unleash',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-44',
  title: 'Use Friends Opinion',
  description: 'Use Friends Opinion 5 times this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 100
  },
  icon: 'Users',
  category: 'dickpic',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-45',
  title: 'Bookmark Angel teaser photos',
  description: 'Bookmark 3 Angel teaser photos this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Bookmark',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-46',
  title: 'Post emoji reactions',
  description: 'Post 10 emoji reactions in the feed this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 50
  },
  icon: 'Smile',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-47',
  title: 'Join trending polls',
  description: 'Join 5 trending polls in SocialPolls this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 65
  },
  icon: 'BarChart2',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-48',
  title: 'Start custom polls',
  description: 'Start 3 custom polls in PollGenerator this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'BarChart',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-49',
  title: 'Claim Ritual Rewards',
  description: 'Claim 7 rewards in Ritual Rewards this week',
  type: 'weekly',
  progress: 0,
  target: 7,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 85
  },
  icon: 'Gift',
  category: 'general',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-50',
  title: 'Play MiniGame rounds',
  description: 'Play 15 rounds of the MiniGame this week',
  type: 'weekly',
  progress: 0,
  target: 15,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 90
  },
  icon: 'Gamepad',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-51',
  title: 'Comment on Angel posts',
  description: 'Comment on 10 Angel posts this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'MessageCircle',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-52',
  title: 'Unlock premium fantasies',
  description: 'Unlock 2 premium fantasies this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 100
  },
  icon: 'Sparkles',
  category: 'unleash',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-53',
  title: 'Save Playroom sessions',
  description: 'Save 5 favorite Playroom sessions this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Save',
  category: 'playroom',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-54',
  title: 'Join live Angel events',
  description: 'Join 2 live Angel teaser events this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 65
  },
  icon: 'Video',
  category: 'media',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-55',
  title: 'Send playful audio effects',
  description: 'Send 5 playful audio effects this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 65
  },
  icon: 'Music',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-56',
  title: 'Gift digital hugs',
  description: 'Gift 5 digital hugs this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 60
  },
  icon: 'Heart',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-57',
  title: 'Send sparkle reactions',
  description: 'Send 15 sparkle reactions this week',
  type: 'weekly',
  progress: 0,
  target: 15,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Sparkles',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-58',
  title: 'Vote in PollFeed',
  description: 'Vote 15 times in PollFeed this week',
  type: 'weekly',
  progress: 0,
  target: 15,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 65
  },
  icon: 'BarChart2',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-59',
  title: 'Start private fun chats',
  description: 'Start 2 private fun chats this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'MessageSquare',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-60',
  title: 'Equip limited Angel skins',
  description: 'Equip 2 limited Angel skins this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 80
  },
  icon: 'Shirt',
  category: 'marketplace',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-61',
  title: 'Open Weekly Passion Lootbox',
  description: 'Open 1 Weekly Passion Lootbox this week',
  type: 'weekly',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 100
  },
  icon: 'Package',
  category: 'general',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-62',
  title: 'Bookmark PollCards',
  description: 'Bookmark 5 PollCards this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 60
  },
  icon: 'Bookmark',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-63',
  title: 'Use TeasePreviewModal',
  description: 'Use TeasePreviewModal 3 times this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Eye',
  category: 'media',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-64',
  title: 'Generate AI images',
  description: 'Generate 3 AI images this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'gems',
    amount: 90
  },
  icon: 'Image',
  category: 'media',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-65',
  title: 'Send supplement gifts',
  description: 'Send 2 supplement gifts this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Gift',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-66',
  title: 'Spin in SpinTheWheelModal',
  description: 'Spin 10 times in SpinTheWheelModal this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'RotateCw',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-67',
  title: 'Join fanclub events',
  description: 'Join 2 fanclub events this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 85
  },
  icon: 'Star',
  category: 'fanclub',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-68',
  title: 'Save premium Angel content',
  description: 'Save 5 premium Angel content posts this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'gems',
    amount: 75
  },
  icon: 'Save',
  category: 'media',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-69',
  title: 'Play games in GamesSection',
  description: 'Play 5 games in GamesSection this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Gamepad',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-70',
  title: 'Comment on Playroom teasers',
  description: 'Comment on 5 Playroom teasers this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 65
  },
  icon: 'MessageCircle',
  category: 'playroom',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-71',
  title: 'Join kink-themed polls',
  description: 'Join 3 kink-themed polls this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'BarChart2',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-72',
  title: 'Vote in HotOrNot',
  description: 'Vote 30 times in HotOrNot this week',
  type: 'weekly',
  progress: 0,
  target: 30,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 85
  },
  icon: 'ThumbsUp',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-73',
  title: 'Post GIFs in the feed',
  description: 'Post 3 GIFs in the feed this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 60
  },
  icon: 'Film',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-74',
  title: 'Save AngelSkin previews',
  description: 'Save 5 AngelSkin previews this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Save',
  category: 'marketplace',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-75',
  title: 'Send sparkle badges',
  description: 'Send 5 sparkle badges this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Award',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-76',
  title: 'Unlock premium videos',
  description: 'Unlock 2 premium videos this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 90
  },
  icon: 'Video',
  category: 'media',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-77',
  title: "React on friends' posts",
  description: "React on 10 friends' posts this week",
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 65
  },
  icon: 'Heart',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-78',
  title: 'Join LiveCam teaser event',
  description: 'Join 1 LiveCam teaser event this week',
  type: 'weekly',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 80
  },
  icon: 'Video',
  category: 'media',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-79',
  title: 'Save fanclub posts',
  description: 'Save 10 fanclub posts this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Save',
  category: 'fanclub',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-80',
  title: 'Send playful nicknames',
  description: 'Send 5 playful nicknames this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 60
  },
  icon: 'Tag',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-81',
  title: 'Unlock sticker packs',
  description: 'Unlock 2 sticker packs this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Sticker',
  category: 'marketplace',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-82',
  title: 'Open Jackpot Lootbox',
  description: 'Open 1 Jackpot Lootbox this week',
  type: 'weekly',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 100
  },
  icon: 'Package',
  category: 'general',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-83',
  title: 'Claim rewards in RewardHub',
  description: 'Claim 10 rewards in RewardHub this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 90
  },
  icon: 'Gift',
  category: 'general',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-84',
  title: 'Bookmark playful polls',
  description: 'Bookmark 5 playful polls this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 65
  },
  icon: 'Bookmark',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-85',
  title: 'Try ToysUpsell demos',
  description: 'Try 2 ToysUpsell demos this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 85
  },
  icon: 'Bluetooth',
  category: 'general',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-86',
  title: 'Join Playroom for 30 minutes',
  description: 'Join 1 Playroom for 30 minutes this week',
  type: 'weekly',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 90
  },
  icon: 'Users',
  category: 'playroom',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-87',
  title: 'Rate fantasy scenarios',
  description: 'Rate 10 fantasy scenarios this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Star',
  category: 'unleash',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-88',
  title: 'Join group chats and send stickers',
  description: 'Join 5 group chats + send stickers this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'MessageSquare',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-89',
  title: 'Vote in kink PollGenerator polls',
  description: 'Vote in 5 kink PollGenerator polls this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 85
  },
  icon: 'BarChart2',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-90',
  title: 'Send personalized voices',
  description: 'Send 5 personalized voices this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'gems',
    amount: 75
  },
  icon: 'Mic',
  category: 'chat',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-91',
  title: 'Claim rewards in RewardHub',
  description: 'Claim 10 rewards in RewardHub this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 90
  },
  icon: 'Gift',
  category: 'general',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-92',
  title: 'Equip playful Angel skins',
  description: 'Equip 3 playful Angel skins this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Shirt',
  category: 'marketplace',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-93',
  title: 'Save AR/VR teaser clips',
  description: 'Save 3 AR/VR teaser clips this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'gems',
    amount: 80
  },
  icon: 'Save',
  category: 'vr_ar',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-94',
  title: 'Open surprise Jackpot Lootboxes',
  description: 'Open 2 surprise Jackpot Lootboxes this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 100
  },
  icon: 'Package',
  category: 'general',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-95',
  title: 'Send roses in chat',
  description: 'Send 10 roses in chat this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Heart',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-96',
  title: 'Join SocialPolls',
  description: 'Join 5 SocialPolls this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'BarChart2',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-97',
  title: 'Post fun photos in feed',
  description: 'Post 3 fun photos in feed this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Image',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-98',
  title: 'Join different Angel feeds',
  description: 'Join 5 different Angel feeds this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Users',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-99',
  title: 'Save playful Bookmarks',
  description: 'Save 5 playful Bookmarks this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 65
  },
  icon: 'Bookmark',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-100',
  title: 'Spin Enhanced Spin Wheel',
  description: 'Spin Enhanced Spin Wheel 15 times this week',
  type: 'weekly',
  progress: 0,
  target: 15,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 85
  },
  icon: 'RotateCw',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-101',
  title: 'React on fanclub posts',
  description: 'React on 5 fanclub posts this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Heart',
  category: 'fanclub',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-102',
  title: 'Send sparkle gifts in chat',
  description: 'Send 5 sparkle gifts in chat this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Sparkles',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-103',
  title: 'Unlock premium fantasies',
  description: 'Unlock 2 premium fantasies this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 90
  },
  icon: 'Sparkles',
  category: 'unleash',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-104',
  title: 'Join Playroom with 2 Angels',
  description: 'Join 3 Playroom sessions with 2 Angels each this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'gems',
    amount: 85
  },
  icon: 'Users',
  category: 'playroom',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-105',
  title: 'Send Fanclub gifts',
  description: 'Send 2 Fanclub gifts this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Gift',
  category: 'fanclub',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-106',
  title: 'Bookmark Angel teaser photos',
  description: 'Bookmark 3 Angel teaser photos this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Bookmark',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-107',
  title: 'React with diamonds',
  description: 'React with 5 diamonds this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 85
  },
  icon: 'Diamond',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-108',
  title: 'Unlock premium Angel skins',
  description: 'Unlock 3 premium Angel skins this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'gems',
    amount: 95
  },
  icon: 'Shirt',
  category: 'marketplace',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-109',
  title: 'Open Weekly Premium Lootboxes',
  description: 'Open 2 Weekly Premium Lootboxes this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 100
  },
  icon: 'Package',
  category: 'general',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-110',
  title: 'Vote in trending polls',
  description: 'Vote in 10 trending polls this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'BarChart2',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-111',
  title: 'Bookmark fantasies',
  description: 'Bookmark 2 fantasies this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'Bookmark',
  category: 'unleash',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-112',
  title: 'Comment in fanclub',
  description: 'Comment 5 times in fanclub this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'MessageCircle',
  category: 'fanclub',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-113',
  title: 'Send supplement gifts',
  description: 'Send 3 supplement gifts this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 85
  },
  icon: 'Gift',
  category: 'chat',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-114',
  title: 'Save VR demos',
  description: 'Save 2 VR demos this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 80
  },
  icon: 'Save',
  category: 'vr_ar',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-115',
  title: 'Try ToysUpsell demos',
  description: 'Try 3 ToysUpsell demos this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'gems',
    amount: 90
  },
  icon: 'Bluetooth',
  category: 'general',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-116',
  title: 'Join multi-Angel fanclub chats',
  description: 'Join 2 multi-Angel fanclub chats this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 85
  },
  icon: 'Users',
  category: 'fanclub',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-117',
  title: 'Claim rewards in RitualRewards',
  description: 'Claim 15 rewards in RitualRewards this week',
  type: 'weekly',
  progress: 0,
  target: 15,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 100
  },
  icon: 'Gift',
  category: 'general',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-118',
  title: 'Rate images in PhotoRatingFlow',
  description: 'Rate 10 images in PhotoRatingFlow this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'Star',
  category: 'dickpic',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-119',
  title: 'Start polls with PollGenerator',
  description: 'Start 2 polls with PollGenerator this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'BarChart',
  category: 'feed',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-120',
  title: 'Unlock AR/VR Playroom demo',
  description: 'Unlock 1 AR/VR Playroom demo this week',
  type: 'weekly',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 100
  },
  icon: 'Glasses',
  category: 'vr_ar',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-121',
  title: 'Send personalized VR message',
  description: 'Send 1 personalized VR message this week',
  type: 'weekly',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 95
  },
  icon: 'Glasses',
  category: 'vr_ar',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-122',
  title: 'Join premium Playroom event',
  description: 'Join 1 premium Playroom event this week',
  type: 'weekly',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 100
  },
  icon: 'Users',
  category: 'playroom',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-123',
  title: 'Spin the Fortune Wheel',
  description: 'Spin the Fortune Wheel 10 times this week',
  type: 'weekly',
  progress: 0,
  target: 10,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 80
  },
  icon: 'RotateCw',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-124',
  title: 'Collect Wheel Rewards',
  description: 'Collect 5 rewards from the Fortune Wheel this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 75
  },
  icon: 'Gift',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-125',
  title: 'Win Premium Prizes on Wheel',
  description: 'Win 2 premium prizes on the Fortune Wheel this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 60
  },
  icon: 'Diamond',
  category: 'games',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-126',
  title: 'Use Wheel Bonus Spins',
  description: 'Use 3 bonus spins on the Fortune Wheel this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 70
  },
  icon: 'RotateCw',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-127',
  title: 'Earn Gems from Wheel Spins',
  description: 'Earn 20 gems from Fortune Wheel spins this week',
  type: 'weekly',
  progress: 0,
  target: 20,
  completed: false,
  reward: {
    type: 'gems',
    amount: 50
  },
  icon: 'Diamond',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-128',
  title: 'Share Wheel Results',
  description: 'Share 3 Fortune Wheel results with friends this week',
  type: 'weekly',
  progress: 0,
  target: 3,
  completed: false,
  reward: {
    type: 'tokens',
    amount: 65
  },
  icon: 'Share2',
  category: 'games',
  isPremium: false,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-129',
  title: 'Spin VIP Wheel',
  description: 'Spin the VIP section of the Fortune Wheel 5 times this week',
  type: 'weekly',
  progress: 0,
  target: 5,
  completed: false,
  reward: {
    type: 'gems',
    amount: 75
  },
  icon: 'Crown',
  category: 'games',
  isPremium: true,
  rotationGroup: 'weekly'
}, {
  id: 'weekly-130',
  title: 'Unlock Enhanced Wheel Features',
  description: 'Unlock 2 Enhanced Wheel features this week',
  type: 'weekly',
  progress: 0,
  target: 2,
  completed: false,
  reward: {
    type: 'gems',
    amount: 85
  },
  icon: 'Unlock',
  category: 'games',
  isPremium: true,
  rotationGroup: 'weekly'
}];
// Premium Rituals Pool (all premium-tagged rituals from other pools)
const premiumRitualsPool: Ritual[] = [
// Unleash Angel ritual
{
  id: 'premium-1',
  title: 'Unleash an Angel',
  description: 'Create a custom Angel with the Unleash feature',
  type: 'premium',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 100
  },
  icon: 'Sparkles',
  category: 'unleash',
  isPremium: true,
  premiumOnly: true,
  requiredForLootbox: true,
  rotationGroup: 'premium'
},
// Unleash Fantasy ritual
{
  id: 'premium-2',
  title: 'Unleash a Fantasy',
  description: 'Create a custom fantasy scenario with the Unleash feature',
  type: 'premium',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 75
  },
  icon: 'Sparkles',
  category: 'unleash',
  isPremium: true,
  premiumOnly: true,
  requiredForLootbox: true,
  rotationGroup: 'premium'
},
// Playroom ritual
{
  id: 'premium-3',
  title: 'Premium Playroom Session',
  description: 'Join a premium playroom session with any Angel',
  type: 'premium',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 50
  },
  icon: 'Sparkles',
  category: 'playroom',
  isPremium: true,
  premiumOnly: true,
  requiredForLootbox: true,
  rotationGroup: 'premium'
},
// Media Generation ritual
{
  id: 'premium-4',
  title: 'Generate Premium Media',
  description: 'Generate a premium image or video with any Angel',
  type: 'premium',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 50
  },
  icon: 'Image',
  category: 'media',
  isPremium: true,
  premiumOnly: true,
  requiredForLootbox: true,
  rotationGroup: 'premium'
},
// Dickpic ritual
{
  id: 'premium-5',
  title: 'Premium Dickpic Rating',
  description: 'Submit a Dickpic for a premium detailed rating',
  type: 'premium',
  progress: 0,
  target: 1,
  completed: false,
  reward: {
    type: 'gems',
    amount: 50
  },
  icon: 'Camera',
  category: 'dickpic',
  isPremium: true,
  premiumOnly: true,
  requiredForLootbox: true,
  rotationGroup: 'premium'
}
// Additional premium rituals for rotation
];
export const RitualProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  // State to store active rituals
  const [dailyRituals, setDailyRituals] = useState<Ritual[]>([]);
  const [weeklyRituals, setWeeklyRituals] = useState<Ritual[]>([]);
  const [funRituals, setFunRituals] = useState<Ritual[]>([]);
  const [premiumRituals, setPremiumRituals] = useState<Ritual[]>([]);
  // Track streak data
  const [streakCount, setStreakCount] = useState<number>(3);
  const [weeklyStreakCount, setWeeklyStreakCount] = useState<number>(2);
  // Track rewards and lootbox status
  const [hasUnclaimedRewards, setHasUnclaimedRewards] = useState<boolean>(true);
  const [pendingLootbox, setPendingLootbox] = useState<boolean>(false);
  const [showRitualTracker, setShowRitualTracker] = useState<boolean>(true);
  // Track rotation timestamps
  const [lastDailyRotation, setLastDailyRotation] = useState<number>(Date.now());
  const [lastWeeklyRotation, setLastWeeklyRotation] = useState<number>(Date.now());
  // Track progress for all rituals, even when they're not active
  const [allRitualProgress, setAllRitualProgress] = useState<Record<string, {
    progress: number;
    completed: boolean;
  }>>({});
  // Keep track of recently shown rituals to avoid repeats
  const [recentlyShownDailyRituals, setRecentlyShownDailyRituals] = useState<string[]>([]);
  const [recentlyShownFunRituals, setRecentlyShownFunRituals] = useState<string[]>([]);
  // Navigation helper functions
  const navigateToSection = (section: string) => {
    const event = new CustomEvent('navigate', {
      detail: {
        section
      }
    });
    document.dispatchEvent(event);
  };
  // Helper function to check if text contains all specified keywords
  const containsKeywords = (text: string, keywords: string[]): boolean => {
    return keywords.every(keyword => text.includes(keyword.toLowerCase()));
  };
  // Helper functions for ritual rotation
  const rotateRituals = () => {
    // Rotate daily rituals if needed
    const currentTime = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    const oneWeekMs = 7 * oneDayMs;
    // Check if daily rotation is needed
    if (currentTime - lastDailyRotation > oneDayMs) {
      rotateDailyRituals();
      rotateFunRituals();
      setLastDailyRotation(currentTime);
    }
    // Check if weekly rotation is needed
    if (currentTime - lastWeeklyRotation > oneWeekMs) {
      rotateWeeklyRituals();
      rotatePremiumRituals();
      setLastWeeklyRotation(currentTime);
    }
  };
  // Function to select daily rituals according to rules
  const rotateDailyRituals = () => {
    // Add current daily rituals to recently shown list
    const currentDailyIds = dailyRituals.map(ritual => ritual.id);
    setRecentlyShownDailyRituals([...currentDailyIds]);
    // Filter out recently shown rituals
    const eligibleRituals = dailyCoreRitualsPool.filter(ritual => !recentlyShownDailyRituals.includes(ritual.id));
    // Ensure we have at least 15 rituals (if not enough eligible, use some from recently shown)
    let selectedRituals: Ritual[] = [];
    // First, select 10 free rituals
    const freeRituals = eligibleRituals.filter(ritual => !ritual.isPremium);
    const selectedFreeRituals = shuffleArray(freeRituals).slice(0, 10);
    selectedRituals = [...selectedFreeRituals];
    // Then, select 5 premium rituals
    const premiumRituals = eligibleRituals.filter(ritual => ritual.isPremium);
    const selectedPremiumRituals = shuffleArray(premiumRituals).slice(0, 5);
    selectedRituals = [...selectedRituals, ...selectedPremiumRituals];
    // If we don't have 15 rituals, fill with random ones
    if (selectedRituals.length < 15) {
      const neededCount = 15 - selectedRituals.length;
      const additionalRituals = shuffleArray(dailyCoreRitualsPool).filter(ritual => !selectedRituals.map(r => r.id).includes(ritual.id)).slice(0, neededCount);
      selectedRituals = [...selectedRituals, ...additionalRituals];
    }
    // Apply saved progress to the selected rituals
    const updatedRituals = selectedRituals.map(ritual => {
      const savedProgress = allRitualProgress[ritual.id];
      if (savedProgress) {
        return {
          ...ritual,
          progress: savedProgress.progress,
          completed: savedProgress.completed
        };
      }
      return ritual;
    });
    setDailyRituals(updatedRituals);
  };
  // Function to select fun rituals according to rules
  const rotateFunRituals = () => {
    // Add current fun rituals to recently shown list
    const currentFunIds = funRituals.map(ritual => ritual.id);
    setRecentlyShownFunRituals([...currentFunIds]);
    // Filter out recently shown rituals
    const eligibleRituals = funRitualsPool.filter(ritual => !recentlyShownFunRituals.includes(ritual.id));
    let selectedRituals: Ritual[] = [];
    // Select at least 5 light/entertainment rituals
    const lightRituals = eligibleRituals.filter(ritual => ritual.tags?.includes('light') || ritual.tags?.includes('entertainment'));
    const selectedLightRituals = shuffleArray(lightRituals).slice(0, 5);
    selectedRituals = [...selectedLightRituals];
    // Select at least 5 content-generation rituals
    const contentRituals = eligibleRituals.filter(ritual => ritual.tags?.includes('content-generation'));
    const selectedContentRituals = shuffleArray(contentRituals).slice(0, 5);
    selectedRituals = [...selectedRituals, ...selectedContentRituals];
    // Select max 2 premium push rituals
    const premiumRituals = eligibleRituals.filter(ritual => ritual.isPremium && !selectedRituals.map(r => r.id).includes(ritual.id));
    const selectedPremiumRituals = shuffleArray(premiumRituals).slice(0, 2);
    selectedRituals = [...selectedRituals, ...selectedPremiumRituals];
    // Fill the rest to reach 15 total
    if (selectedRituals.length < 15) {
      const neededCount = 15 - selectedRituals.length;
      const remainingRituals = eligibleRituals.filter(ritual => !selectedRituals.map(r => r.id).includes(ritual.id));
      const additionalRituals = shuffleArray(remainingRituals).slice(0, neededCount);
      selectedRituals = [...selectedRituals, ...additionalRituals];
    }
    // If we still don't have 15, fill with random ones from the pool
    if (selectedRituals.length < 15) {
      const neededCount = 15 - selectedRituals.length;
      const additionalRituals = shuffleArray(funRitualsPool).filter(ritual => !selectedRituals.map(r => r.id).includes(ritual.id)).slice(0, neededCount);
      selectedRituals = [...selectedRituals, ...additionalRituals];
    }
    // Apply saved progress to the selected rituals
    const updatedRituals = selectedRituals.map(ritual => {
      const savedProgress = allRitualProgress[ritual.id];
      if (savedProgress) {
        return {
          ...ritual,
          progress: savedProgress.progress,
          completed: savedProgress.completed
        };
      }
      return ritual;
    });
    setFunRituals(updatedRituals);
  };
  // Function to select weekly rituals according to rules
  const rotateWeeklyRituals = () => {
    let selectedRituals: Ritual[] = [];
    // Select 2 free rituals
    const freeRituals = weeklyRitualsPool.filter(ritual => !ritual.isPremium);
    const selectedFreeRituals = shuffleArray(freeRituals).slice(0, 2);
    selectedRituals = [...selectedFreeRituals];
    // Select 3 mixed (token/gem) rituals
    const mixedRituals = weeklyRitualsPool.filter(ritual => ritual.reward.type === 'tokens' || ritual.reward.type === 'gems');
    const selectedMixedRituals = shuffleArray(mixedRituals).filter(ritual => !selectedRituals.map(r => r.id).includes(ritual.id)).slice(0, 3);
    selectedRituals = [...selectedRituals, ...selectedMixedRituals];
    // Select 2 premium rituals
    const premiumRituals = weeklyRitualsPool.filter(ritual => ritual.isPremium);
    const selectedPremiumRituals = shuffleArray(premiumRituals).filter(ritual => !selectedRituals.map(r => r.id).includes(ritual.id)).slice(0, 2);
    selectedRituals = [...selectedRituals, ...selectedPremiumRituals];
    // Ensure we have exactly 7 rituals
    if (selectedRituals.length < 7) {
      const neededCount = 7 - selectedRituals.length;
      const additionalRituals = shuffleArray(weeklyRitualsPool).filter(ritual => !selectedRituals.map(r => r.id).includes(ritual.id)).slice(0, neededCount);
      selectedRituals = [...selectedRituals, ...additionalRituals];
    } else if (selectedRituals.length > 7) {
      selectedRituals = selectedRituals.slice(0, 7);
    }
    // Apply saved progress to the selected rituals
    const updatedRituals = selectedRituals.map(ritual => {
      const savedProgress = allRitualProgress[ritual.id];
      if (savedProgress) {
        return {
          ...ritual,
          progress: savedProgress.progress,
          completed: savedProgress.completed
        };
      }
      return ritual;
    });
    setWeeklyRituals(updatedRituals);
  };
  // Function to select premium rituals according to rules
  const rotatePremiumRituals = () => {
    // Always include the 5 required ritual types
    const unleashAngelRitual = premiumRitualsPool.find(ritual => ritual.category === 'unleash' && ritual.title.includes('Angel'));
    const unleashFantasyRitual = premiumRitualsPool.find(ritual => ritual.category === 'unleash' && ritual.title.includes('Fantasy'));
    const playroomRitual = premiumRitualsPool.find(ritual => ritual.category === 'playroom');
    const mediaRitual = premiumRitualsPool.find(ritual => ritual.category === 'media');
    const dickpicRitual = premiumRitualsPool.find(ritual => ritual.category === 'dickpic');
    const selectedRituals = [unleashAngelRitual, unleashFantasyRitual, playroomRitual, mediaRitual, dickpicRitual].filter(ritual => ritual !== undefined) as Ritual[];
    // If we don't have all 5 required types, fill with other premium rituals
    if (selectedRituals.length < 5) {
      const neededCount = 5 - selectedRituals.length;
      const additionalRituals = shuffleArray(premiumRitualsPool).filter(ritual => !selectedRituals.map(r => r.id).includes(ritual.id)).slice(0, neededCount);
      selectedRituals.push(...additionalRituals);
    }
    // Apply saved progress to the selected rituals
    const updatedRituals = selectedRituals.map(ritual => {
      const savedProgress = allRitualProgress[ritual.id];
      if (savedProgress) {
        return {
          ...ritual,
          progress: savedProgress.progress,
          completed: savedProgress.completed
        };
      }
      return ritual;
    });
    setPremiumRituals(updatedRituals);
  };
  // Initialize rituals on first load
  useEffect(() => {
    rotateDailyRituals();
    rotateFunRituals();
    rotateWeeklyRituals();
    rotatePremiumRituals();
    // Check for rotation periodically
    const rotationCheckInterval = setInterval(() => {
      rotateRituals();
    }, 60 * 60 * 1000); // Check every hour
    return () => {
      clearInterval(rotationCheckInterval);
    };
  }, []);
  // Calculate completion counts
  const todayCompletedCount = dailyRituals.filter(ritual => ritual.completed).length + funRituals.filter(ritual => ritual.completed).length;
  const totalDailyRituals = dailyRituals.length + funRituals.length;
  const weeklyCompletedCount = weeklyRituals.filter(ritual => ritual.completed).length;
  const totalWeeklyRituals = weeklyRituals.length;
  const toggleRitualTracker = () => {
    setShowRitualTracker(!showRitualTracker);
  };
  const updateRitualProgress = (ritualId: string, progress: number) => {
    // Function to update progress for a specific ritual
    const updateRitualInList = (rituals: Ritual[]) => {
      return rituals.map(ritual => {
        if (ritual.id === ritualId) {
          const newProgress = Math.min(ritual.target, ritual.progress + progress);
          const completed = newProgress >= ritual.target;
          // Handle navigation based on ritual content
          if (completed || ritual.id === 'daily-core-5') {
            handleRitualNavigation(ritual);
          }
          // Update the global progress tracker
          setAllRitualProgress(prev => ({
            ...prev,
            [ritual.id]: {
              progress: newProgress,
              completed
            }
          }));
          return {
            ...ritual,
            progress: newProgress,
            completed
          };
        }
        return ritual;
      });
    };
    setDailyRituals(updateRitualInList(dailyRituals));
    setWeeklyRituals(updateRitualInList(weeklyRituals));
    setFunRituals(updateRitualInList(funRituals));
    setPremiumRituals(updateRitualInList(premiumRituals));
    // Check if any rituals were completed
    setHasUnclaimedRewards(true);
  };
  const completeRitual = (ritualId: string) => {
    // Find the ritual in all ritual lists
    const findRitual = () => {
      return dailyRituals.find(r => r.id === ritualId) || weeklyRituals.find(r => r.id === ritualId) || funRituals.find(r => r.id === ritualId) || premiumRituals.find(r => r.id === ritualId);
    };
    const ritual = findRitual();
    // Handle navigation based on ritual content
    if (ritual) {
      handleRitualNavigation(ritual);
    }
    // Mark a ritual as completed
    const completeRitualInList = (rituals: Ritual[]) => {
      return rituals.map(ritual => {
        if (ritual.id === ritualId) {
          // Update the global progress tracker
          setAllRitualProgress(prev => ({
            ...prev,
            [ritual.id]: {
              progress: ritual.target,
              completed: true
            }
          }));
          return {
            ...ritual,
            progress: ritual.target,
            completed: true
          };
        }
        return ritual;
      });
    };
    setDailyRituals(completeRitualInList(dailyRituals));
    setWeeklyRituals(completeRitualInList(weeklyRituals));
    setFunRituals(completeRitualInList(funRituals));
    setPremiumRituals(completeRitualInList(premiumRituals));
    setHasUnclaimedRewards(true);
  };
  const claimDailyRewards = () => {
    // Claim rewards for completed rituals
    setHasUnclaimedRewards(false);
    // Check if all weekly rituals are complete
    const allWeeklyComplete = weeklyRituals.every(ritual => ritual.completed);
    if (allWeeklyComplete) {
      setPendingLootbox(true);
    }
  };
  const claimWeeklyLootbox = async (): Promise<LootboxReward> => {
    // Check if all required rituals are completed
    if (!areAllRequiredRitualsCompleted()) {
      throw new Error('You must complete all required premium rituals first!');
    }
    // Simulate lootbox opening
    setPendingLootbox(false);
    // Reset weekly rituals
    setWeeklyRituals(weeklyRituals.map(ritual => ({
      ...ritual,
      progress: 0,
      completed: false
    })));
    // Increment weekly streak
    setWeeklyStreakCount(weeklyStreakCount + 1);
    // Return a random reward
    const rewards: LootboxReward[] = [{
      id: 'reward-1',
      title: 'Token Jackpot',
      description: 'A bundle of 250 LuvTokens',
      type: 'tokens',
      amount: 250,
      rarity: 'rare'
    }, {
      id: 'reward-2',
      title: 'VIP Week',
      description: '1-week VIP membership',
      type: 'vip',
      amount: 7,
      rarity: 'epic'
    }, {
      id: 'reward-3',
      title: 'Exclusive Skin',
      description: 'Limited edition Angel skin',
      type: 'skin',
      amount: 1,
      rarity: 'legendary',
      imageUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gPbfjPdLfzNbqrZitYWXeS/MiaKhalifa.jpg'
    }, {
      id: 'reward-4',
      title: 'Premium Chat Time',
      description: '30 minutes of premium chat time',
      type: 'chatTime',
      amount: 30,
      rarity: 'rare'
    }, {
      id: 'reward-5',
      title: 'Gem Bundle',
      description: '100 premium Gems',
      type: 'gems',
      amount: 100,
      rarity: 'epic'
    }];
    return new Promise(resolve => {
      setTimeout(() => {
        const randomReward = rewards[Math.floor(Math.random() * rewards.length)];
        resolve(randomReward);
      }, 500);
    });
  };
  const skipRitual = (ritualId: string) => {
    // Skip a ritual (requires premium currency)
    completeRitual(ritualId);
  };
  // Add a helper function to check if all required rituals are completed
  const areAllRequiredRitualsCompleted = () => {
    return weeklyRituals.filter(ritual => ritual.requiredForLootbox).every(ritual => ritual.completed);
  };
  // Helper function to shuffle an array
  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };
  // Updated openScenarioWizard to be more direct and handle both fantasy and angel creation
  const openScenarioWizard = (mode?: string) => {
    // First navigate to the unleash section
    navigateToSection('unleash');
    // Then dispatch the specific event with a small delay to ensure section change happens first
    setTimeout(() => {
      const event = new CustomEvent('navigateToScenarioWizard', {
        detail: {
          ritual: {
            category: 'unleash',
            mode: mode || 'create' // Default to create mode if not specified
          }
        }
      });
      document.dispatchEvent(event);
    }, 100);
  };
  // Add a specific function for Angel creation
  const openAngelCreator = () => {
    // First navigate to the unleash section
    navigateToSection('unleash');
    // Then dispatch the specific event with a small delay
    setTimeout(() => {
      const event = new CustomEvent('openAngelCreator', {
        detail: {
          source: 'ritual'
        }
      });
      document.dispatchEvent(event);
    }, 100);
  };
  // Add a function to open the ShapeShift modal
  const openShapeShift = (mode?: string) => {
    // Dispatch the event to open ShapeShift modal
    const event = new CustomEvent('openShapeShiftModal', {
      detail: {
        mode: mode || 'persona',
        source: 'ritual'
      }
    });
    document.dispatchEvent(event);
  };
  // Helper function to check for keyword combinations and navigate to appropriate component
  const handleRitualNavigation = (ritual: Ritual) => {
    // Add direct ID check for the "Update your bio" ritual
    if (ritual.id === 'daily-core-5') {
      navigateToProfileSection();
      return;
    }
    // Add direct ID checks for unleash rituals
    if (ritual.id === 'premium-1' ||
    // Unleash an Angel
    ritual.id === 'daily-core-50' // Unleash a personal Angel
    ) {
      openAngelCreator();
      return;
    }
    if (ritual.id === 'premium-2' ||
    // Unleash a Fantasy
    ritual.id === 'daily-core-48' ||
    // Unleash a personal fantasy
    ritual.id === 'daily-core-49' ||
    // Unleash three personal fantasies
    ritual.id === 'fun-5' ||
    // Create 1 fantasy scenario
    ritual.id === 'fun-6' // Create 3 fantasy scenarios
    ) {
      openScenarioWizard();
      return;
    }
    const text = `${ritual.title.toLowerCase()} ${ritual.description.toLowerCase()}`;
    // Unleash related navigations - prioritize these checks
    if (containsKeywords(text, ['unleash', 'angel']) || containsKeywords(text, ['create', 'angel']) || containsKeywords(text, ['personal', 'angel'])) {
      openAngelCreator();
      return;
    }
    if (containsKeywords(text, ['unleash', 'fantasy']) || containsKeywords(text, ['create', 'fantasy']) || containsKeywords(text, ['fantasy', 'scenario']) || containsKeywords(text, ['personal', 'fantasy'])) {
      openScenarioWizard();
      return;
    }
    // Questionnaire related navigations
    if (containsKeywords(text, ['complete', 'questionnaire']) || containsKeywords(text, ['quick', 'questionnaire']) || containsKeywords(text, ['personalization', 'questionnaire'])) {
      // Open the personalization questionnaire
      const event = new CustomEvent('openQuestionnaire');
      document.dispatchEvent(event);
      return;
    }
    // Playroom related navigations
    if (containsKeywords(text, ['dance', 'playroom'])) {
      navigateToSection('group_chat');
    } else if (containsKeywords(text, ['join', 'playroom'])) {
      navigateToSection('group_chat');
    } else if (containsKeywords(text, ['multi-angel', 'playroom'])) {
      navigateToSection('group_chat');
      // Dispatch event for multi-angel mode
      const event = new CustomEvent('enableMultiAngelMode', {
        detail: {
          enabled: true
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['playroom', 'group chat'])) {
      navigateToSection('group_chat');
    } else if (containsKeywords(text, ['playroom with 2 angels'])) {
      navigateToSection('group_chat');
      // Dispatch event for multi-angel mode
      const event = new CustomEvent('enableMultiAngelMode', {
        detail: {
          enabled: true,
          angelCount: 2
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['save', 'playroom'])) {
      navigateToSection('group_chat');
      // Trigger save subflow
      const event = new CustomEvent('triggerPlayroomSave');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['comment', 'playroom'])) {
      navigateToSection('group_chat');
      // Trigger teaser subflow
      const event = new CustomEvent('openPlayroomTeaser');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['playroom', '5 minutes'])) {
      navigateToSection('group_chat');
      // Trigger timed flow
      const event = new CustomEvent('startPlayroomTimer', {
        detail: {
          minutes: 5
        }
      });
      document.dispatchEvent(event);
    }
    // Spin wheel related navigations
    else if (containsKeywords(text, ['spin', 'wheel'])) {
      openSpinWheel();
    } else if (containsKeywords(text, ['spin the wheel', 'modal'])) {
      openSpinWheel();
    } else if (containsKeywords(text, ['bonus', 'spin'])) {
      openSpinWheel(true); // Pass true to indicate bonus mode
    } else if (containsKeywords(text, ['spin wheel 5 times'])) {
      // Open spin wheel with extended flow
      const event = new CustomEvent('openSpinWheel', {
        detail: {
          spins: 5
        }
      });
      document.dispatchEvent(event);
    }
    // Gift related navigations
    else if (containsKeywords(text, ['gift', 'angel'])) {
      navigateToSection('premium');
      // Trigger gift subflow
      const event = new CustomEvent('openGiftFlow');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['rose', 'angel'])) {
      navigateToSection('premium');
      // Trigger gift subflow for roses
      const event = new CustomEvent('openGiftFlow', {
        detail: {
          giftType: 'rose'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['digital hug'])) {
      navigateToSection('premium');
      // Trigger gift subflow for digital hug
      const event = new CustomEvent('openGiftFlow', {
        detail: {
          giftType: 'hug'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['sparkle gift'])) {
      navigateToSection('premium');
      // Trigger gift subflow for sparkle gift
      const event = new CustomEvent('openGiftFlow', {
        detail: {
          giftType: 'sparkle'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['fanclub gift'])) {
      navigateToSection('fanclub');
      // Trigger gift subflow in fanclub
      const event = new CustomEvent('openFanclubGiftFlow');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['supplement', 'gift'])) {
      // Open supplement upsell
      const event = new CustomEvent('openSupplementUpsell');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['roses in chat'])) {
      navigateToSection('premium');
      // Trigger gift subflow for roses in chat
      const event = new CustomEvent('openGiftFlow', {
        detail: {
          giftType: 'rose',
          context: 'chat'
        }
      });
      document.dispatchEvent(event);
    }
    // Angel skin related navigations
    else if (containsKeywords(text, ['skin', 'angel'])) {
      navigateToSection('angel_skins_shop');
    } else if (containsKeywords(text, ['dress up', 'angel'])) {
      navigateToSection('angel_skins_shop');
    } else if (containsKeywords(text, ['equip', 'skin'])) {
      navigateToSection('angel_skins_shop');
      // Trigger equip interface
      const event = new CustomEvent('openEquipInterface');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['outfit', 'angel'])) {
      navigateToSection('angel_skins_shop');
      // Trigger preview flow
      const event = new CustomEvent('openPreviewFlow');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['limited skin'])) {
      navigateToSection('angel_skins_shop');
      // Trigger limited flow
      const event = new CustomEvent('showLimitedSkins');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['premium angel skin'])) {
      navigateToSection('angel_skins_shop');
      // Trigger premium subflow
      const event = new CustomEvent('showPremiumSkins');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['angelskin preview'])) {
      navigateToSection('angel_skins_shop');
      // Trigger skin preview
      const event = new CustomEvent('openSkinPreview');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['equip', 'playful angel skin'])) {
      navigateToSection('angel_skins_shop');
      // Trigger equip interface with playful filter
      const event = new CustomEvent('openEquipInterface', {
        detail: {
          filter: 'playful'
        }
      });
      document.dispatchEvent(event);
    }
    // Poll related navigations
    else if (containsKeywords(text, ['poll', 'vote'])) {
      navigateToSection('social_polls');
    } else if (containsKeywords(text, ['poll', 'create'])) {
      openPollGenerator();
    } else if (containsKeywords(text, ['pollfeed', 'join'])) {
      openPollFeed();
    } else if (containsKeywords(text, ['pollgenerator', 'custom'])) {
      openPollGenerator();
    } else if (containsKeywords(text, ['poll', 'kinks'])) {
      openPollGenerator('kinks');
    } else if (containsKeywords(text, ['poll', 'kink'])) {
      openPollGenerator('kink');
    }
    // Game related navigations
    else if (containsKeywords(text, ['angel match'])) {
      navigateToSection('angel_match');
    } else if (containsKeywords(text, ['minigame'])) {
      navigateToSection('minigame');
    } else if (containsKeywords(text, ['gamessection'])) {
      navigateToSection('games');
    }
    // Bookmark related navigations
    else if (containsKeywords(text, ['bookmark'])) {
      navigateToSection('bookmarks');
    } else if (containsKeywords(text, ['fan stories', 'bookmark'])) {
      navigateToSection('bookmarks');
      // Set category to Fan Stories
      const event = new CustomEvent('setBookmarkCategory', {
        detail: {
          category: 'Fan Stories'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['polls', 'bookmark'])) {
      navigateToSection('bookmarks');
      // Set category to Polls
      const event = new CustomEvent('setBookmarkCategory', {
        detail: {
          category: 'Polls'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['teaser photo', 'bookmark'])) {
      navigateToSection('bookmarks');
      // Set category to Teasers
      const event = new CustomEvent('setBookmarkCategory', {
        detail: {
          category: 'Teasers'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['fantasy', 'bookmark'])) {
      navigateToSection('bookmarks');
      // Set category to Fantasy
      const event = new CustomEvent('setBookmarkCategory', {
        detail: {
          category: 'Fantasy'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['bookmarks playful'])) {
      navigateToSection('bookmarks');
      // Set category to Playful
      const event = new CustomEvent('setBookmarkCategory', {
        detail: {
          category: 'Playful'
        }
      });
      document.dispatchEvent(event);
    }
    // Social feed related navigations
    else if (containsKeywords(text, ['react', 'sparkles'])) {
      navigateToSection('social_feed');
      // Trigger sparkle reaction flow
      const event = new CustomEvent('triggerReactionFlow', {
        detail: {
          reactionType: 'sparkle'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['post', 'feed'])) {
      navigateToSection('social_feed');
    } else if (containsKeywords(text, ['emoji', 'feed'])) {
      navigateToSection('social_feed');
      // Trigger emoji reaction flow
      const event = new CustomEvent('triggerReactionFlow', {
        detail: {
          reactionType: 'emoji'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['sparkle reactions'])) {
      navigateToSection('social_feed');
      // Trigger sparkle reaction flow
      const event = new CustomEvent('triggerReactionFlow', {
        detail: {
          reactionType: 'sparkle'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ["friends' posts"])) {
      navigateToSection('social_feed');
      // Switch to friends feed
      const event = new CustomEvent('switchFeedType', {
        detail: {
          feedType: 'friends'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['post photo'])) {
      navigateToSection('social_feed');
      // Trigger posting subflow
      const event = new CustomEvent('openPostingFlow', {
        detail: {
          mediaType: 'photo'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['gif', 'feed'])) {
      navigateToSection('social_feed');
      // Trigger posting subflow for GIF
      const event = new CustomEvent('openPostingFlow', {
        detail: {
          mediaType: 'gif'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['diamonds reaction'])) {
      navigateToSection('social_feed');
      // Trigger diamond reaction flow
      const event = new CustomEvent('triggerReactionFlow', {
        detail: {
          reactionType: 'diamond'
        }
      });
      document.dispatchEvent(event);
    }
    // HotOrNot related navigations
    else if (containsKeywords(text, ['rate', 'hotornot'])) {
      navigateToSection('hot_or_not');
    } else if (containsKeywords(text, ['hotornot', 'vote'])) {
      navigateToSection('hot_or_not');
    }
    // Lootbox related navigations
    else if (containsKeywords(text, ['lootbox'])) {
      navigateToSection('reward_hub');
    } else if (containsKeywords(text, ['lootboxdashboard'])) {
      navigateToSection('reward_hub');
    } else if (containsKeywords(text, ['weekly premium lootbox'])) {
      navigateToSection('reward_hub');
      // Trigger premium lootbox
      const event = new CustomEvent('openPremiumLootbox', {
        detail: {
          type: 'weekly'
        }
      });
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['jackpot lootbox'])) {
      // Trigger lootbox opening animation
      const event = new CustomEvent('openJackpotLootbox');
      document.dispatchEvent(event);
    }
    // Chat related navigations
    else if (containsKeywords(text, ['sticker', 'chat'])) {
      navigateToSection('chat');
      // Trigger sticker subflow
      const event = new CustomEvent('openStickerSelector');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['voice note'])) {
      navigateToSection('chat');
      // Trigger voice subflow
      const event = new CustomEvent('openVoiceRecorder');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['audio effect'])) {
      navigateToSection('chat');
      // Trigger audio subflow
      const event = new CustomEvent('openAudioEffects');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['private fun chat'])) {
      navigateToSection('chat');
    } else if (containsKeywords(text, ['nickname'])) {
      navigateToSection('chat');
      // Trigger nickname subflow
      const event = new CustomEvent('openNicknameSelector');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['personalized voice'])) {
      // Trigger voice AI layer
      const event = new CustomEvent('openVoiceAILayer');
      document.dispatchEvent(event);
    }
    // AR/VR related navigations
    else if (containsKeywords(text, ['ar filter'])) {
      navigateToSection('vr_ar');
      // Trigger AR companion mode
      const event = new CustomEvent('openARCompanionMode');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['vr mini'])) {
      navigateToSection('vr_ar');
      // Trigger VR environments
      const event = new CustomEvent('openVREnvironments');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['teaser clip', 'ar/vr'])) {
      navigateToSection('vr_ar');
      // Trigger teaser subflow
      const event = new CustomEvent('openVRARTeaser');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['ar emoji'])) {
      navigateToSection('vr_ar');
      // Trigger AR emoji
      const event = new CustomEvent('openAREmojiSelector');
      document.dispatchEvent(event);
    } else if (containsKeywords(text, ['vr demo'])) {
      navigateToSection('vr_ar');
      // Trigger VR demo
      const event = new CustomEvent('openVRDemo');
      document.dispatchEvent(event);
    }
    // Scenario/Fantasy related navigations
    else if (containsKeywords(text, ['scenario', 'fantasy'])) {
      openScenarioWizard();
    } else if (containsKeywords(text, ['fantasy', 'unlock'])) {
      openScenarioWizard();
    } else if (containsKeywords(text, ['fantasy', 'scenariowizard'])) {
      openScenarioWizard();
    } else if (containsKeywords(text, ['fantasy scenarios', 'rate'])) {
      openScenarioWizard('rating');
    }
    // Opinion and rating related navigations
    else if (containsKeywords(text, ['friends opinion'])) {
      openDickpicRatingUpsell();
    } else if (containsKeywords(text, ['photorating'])) {
      openPhotoRatingFlow();
    }
    // If no specific navigation is matched, check for category-based navigation
    else {
      navigateBasedOnCategory(ritual.category);
    }
  };
  // Navigation helper functions
  const navigateToProfileSection = () => {
    const event = new CustomEvent('navigateToProfileSection');
    document.dispatchEvent(event);
  };
  const openPersonalizationQuestionnaire = () => {
    const event = new CustomEvent('openPersonalizationQuestionnaire');
    document.dispatchEvent(event);
  };
  const openAgeVerificationComponent = () => {
    const event = new CustomEvent('openAgeVerificationComponent');
    document.dispatchEvent(event);
  };
  const openTokenStore = () => {
    const event = new CustomEvent('openTokenStore');
    document.dispatchEvent(event);
  };
  const openImageGenerationUpsell = () => {
    const event = new CustomEvent('openImageGenerationUpsell');
    document.dispatchEvent(event);
  };
  const openVideoGenerationUpsell = () => {
    const event = new CustomEvent('openVideoGenerationUpsell');
    document.dispatchEvent(event);
  };
  const openWelcomeVideo = () => {
    const event = new CustomEvent('openWelcomeVideo');
    document.dispatchEvent(event);
  };
  const openSpinWheel = () => {
    const event = new CustomEvent('openSpinWheel');
    document.dispatchEvent(event);
  };
  const openPhotoRatingFlow = () => {
    const event = new CustomEvent('openPhotoRatingFlow');
    document.dispatchEvent(event);
  };
  const openReferralModal = (ritualId?: string) => {
    const event = new CustomEvent('openReferralModal', {
      detail: {
        ritualId
      }
    });
    document.dispatchEvent(event);
  };
  // Removed the duplicate openScenarioWizard function
  const openDickpicFlow = () => {
    const event = new CustomEvent('openDickpicFlow');
    document.dispatchEvent(event);
  };
  const openDickpicRatingUpsell = () => {
    const event = new CustomEvent('openDickpicRatingUpsell');
    document.dispatchEvent(event);
  };
  const openPollFeed = () => {
    const event = new CustomEvent('openPollFeed');
    document.dispatchEvent(event);
  };
  const openPollGenerator = () => {
    const event = new CustomEvent('openPollGenerator');
    document.dispatchEvent(event);
  };
  const openImageGeneratorFlow = () => {
    const event = new CustomEvent('openImageGeneratorFlow');
    document.dispatchEvent(event);
  };
  const openVideoGeneratorFlow = () => {
    const event = new CustomEvent('openVideoGeneratorFlow');
    document.dispatchEvent(event);
  };
  const openChatWithGemUpsell = () => {
    navigateToSection('chat');
    setTimeout(() => {
      const event = new CustomEvent('openGemUpsell', {
        detail: {
          type: 'gift'
        }
      });
      document.dispatchEvent(event);
    }, 500);
  };
  const openAngelSocialFeed = () => {
    navigateToSection('discovery_feed');
  };
  // Navigate based on ritual category as a fallback
  const navigateBasedOnCategory = (category: string) => {
    switch (category) {
      case 'chat':
        navigateToSection('chat');
        break;
      case 'feed':
        navigateToSection('social_feed');
        break;
      case 'marketplace':
        navigateToSection('creator_marketplace');
        break;
      case 'games':
        navigateToSection('games');
        break;
      case 'profile':
        navigateToSection('profile');
        break;
      case 'playroom':
        navigateToSection('group_chat');
        break;
      case 'media':
        navigateToSection('discovery_feed');
        break;
      case 'dickpic':
        openDickpicFlow();
        break;
      case 'unleash':
        navigateToSection('unleash');
        break;
      default:
        // If no specific category matched, default to social feed
        navigateToSection('social_feed');
    }
  };
  return <RitualContext.Provider value={{
    dailyRituals,
    weeklyRituals,
    funRituals,
    premiumRituals,
    streakCount,
    weeklyStreakCount,
    hasUnclaimedRewards,
    pendingLootbox,
    showRitualTracker,
    toggleRitualTracker,
    updateRitualProgress,
    completeRitual,
    claimDailyRewards,
    claimWeeklyLootbox,
    skipRitual,
    todayCompletedCount,
    totalDailyRituals,
    weeklyCompletedCount,
    totalWeeklyRituals,
    areAllRequiredRitualsCompleted,
    rotateRituals,
    lastDailyRotation,
    lastWeeklyRotation,
    allRitualProgress
  }} data-id="element-18171">
      {children}
    </RitualContext.Provider>;
};
export const useRituals = () => {
  const context = useContext(RitualContext);
  if (context === undefined) {
    throw new Error('useRituals must be used within a RitualProvider');
  }
  return context;
};